import { useCallback, useRef, useLayoutEffect } from "react";
import Video from "twilio-video";
import { useVideoCalling } from "./useVideoCallingContext";
import type { VideoCallingSlice } from "../types";

/**
 * Hook for Twilio Video room connection and event handling.
 *
 * Handles connecting to and disconnecting from a Twilio Video room,
 * and sets up event listeners for participant changes.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { connectToRoom, disconnectFromRoom, isConnected } = useVideoRoom();
 *
 *   return (
 *     <div>
 *       <button onClick={() => connectToRoom(token, roomName)}>Connect</button>
 *       <button onClick={disconnectFromRoom}>Disconnect</button>
 *       <p>{isConnected ? 'Connected' : 'Disconnected'}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useVideoRoom() {
  const videoCalling = useVideoCalling();

  // Use ref to access latest videoCalling without needing it as callback dependency
  // This prevents callbacks from being recreated on every state change
  const videoCallingRef = useRef<VideoCallingSlice>(videoCalling);
  useLayoutEffect(() => {
    videoCallingRef.current = videoCalling;
  });

  /**
   * Connect to a Twilio Video room.
   *
   * @param token - JWT access token from the backend
   * @param roomName - Name of the room to join
   */
  const connectToRoom = useCallback(async (token: string, roomName: string) => {
    const vc = videoCallingRef.current;

    vc.setConnectionState("connecting");
    vc.setConnectionError(null);

    try {
      // Gather local tracks (filter out nulls)
      const tracks = [vc.localVideoTrack, vc.localAudioTrack].filter(Boolean);

      // Connect to the room
      const room = await Video.connect(token, {
        name: roomName,
        tracks: tracks as (import("twilio-video").LocalVideoTrack | import("twilio-video").LocalAudioTrack)[],
        dominantSpeaker: true,
        networkQuality: { local: 1, remote: 1 },
      });

      vc.setRoom(room);
      vc.setConnectionState("connected");

      // Disconnect gracefully when the page unloads so remote participants
      // receive participantDisconnected immediately instead of waiting for
      // Twilio's media timeout. pagehide (not beforeunload) — it only fires
      // on real unloads and works on iOS Safari / bfcache.
      const handlePageHide = () => {
        videoCallingRef.current.resetVideoState();
      };
      window.addEventListener("pagehide", handlePageHide);

      // Set up event listeners (use videoCallingRef to get latest state in callbacks)
      room.on("participantConnected", (participant) => {
        console.log(`Participant connected: ${participant.identity}`);
        videoCallingRef.current.addParticipant(participant);
      });

      room.on("participantDisconnected", (participant) => {
        console.log(`Participant disconnected: ${participant.identity}`);
        videoCallingRef.current.removeParticipant(participant.sid);
      });

      room.on("disconnected", (_, error) => {
        window.removeEventListener("pagehide", handlePageHide);
        const vcRef = videoCallingRef.current;
        if (error) {
          console.error("Room disconnected with error:", error);
          vcRef.setConnectionError(error.message);
        }
        vcRef.setConnectionState("disconnected");
        vcRef.setRoom(null);
        vcRef.clearParticipants();
      });

      // Add existing participants
      room.participants.forEach((participant) => {
        vc.addParticipant(participant);
      });

      return room;
    } catch (error) {
      console.error("Failed to connect to room:", error);
      vc.setConnectionError(error instanceof Error ? error.message : "Failed to connect");
      vc.setConnectionState("error");
      throw error;
    }
  }, []);

  /**
   * Disconnect from the current room.
   */
  const disconnectFromRoom = useCallback(() => {
    const vc = videoCallingRef.current;
    if (vc.room) {
      vc.room.disconnect();
    }
    // State will be updated by the 'disconnected' event handler
  }, []);

  return {
    // State (from Zustand)
    room: videoCalling.room,
    connectionState: videoCalling.connectionState,
    connectionError: videoCalling.connectionError,
    participants: videoCalling.participants,
    isConnected: videoCalling.connectionState === "connected",
    isConnecting: videoCalling.connectionState === "connecting",

    // Actions
    connectToRoom,
    disconnectFromRoom,
  };
}
